/**
 * VENDOR MODULES
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useUserSessionContext } from 'features/session/userSessionStore';
import { useCreditAvailableQuery } from 'features/tickets/queries';
import { formatCurrency } from 'shared/money/formatCurrency';

interface UserCreditInfoProps {
  onClose?: () => void;
}

function UserCreditInfo({ onClose }: UserCreditInfoProps) {
  const { t, i18n } = useTranslation();
  const [{ user }] = useUserSessionContext();
  const userObj = user as Record<string, unknown> | null | undefined;
  const isGuest = userObj?.guest === true || !userObj?.session_id || !userObj?.player_id;
  const { data: creditData } = useCreditAvailableQuery({ enabled: !isGuest });
  const creditAvailable = creditData?.credit_available != null
    ? Number(creditData.credit_available)
    : undefined;
  // Display default when balance not yet loaded (single documented fallback).
  const balance = creditAvailable ?? (userObj?.player_balance as number) ?? 0;
  const currency = (userObj?.currency as string) || null;
  const formatted = formatCurrency(balance, currency, (i18n?.language as string) ?? undefined);

  return (
    <div className="bg-card rounded-lg border border-border p-3 shadow-sm">
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0 w-8 h-8 bg-success/20 rounded-full flex items-center justify-center">
          <i className="bi bi-cash-coin text-success text-sm"></i>
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-card-foreground">
            {t('YOUR_CREDIT_IS')} <span className="text-lg font-semibold text-foreground">{formatted}</span>
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="flex-shrink-0 p-1 bg-accent hover:bg-accent/90 text-accent-foreground rounded-full transition-colors"
            aria-label={t('CLOSE')}
          >
            <i className="bi bi-x text-sm"></i>
          </button>
        )}
      </div>
    </div>
  );
}

export default UserCreditInfo;
